import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import platformApi, { PLATFORM_TOKEN_KEY, platformErrorMessage } from '../../services/platformApi'
import './platform.css'

const STATUS_LABEL = {
  pending: 'Pendente',
  provisioning: 'Criando tabelas',
  active: 'Ativa',
  failed: 'Falhou',
  suspended: 'Suspensa',
}

function OrganizationManagePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [organization, setOrganization] = useState(null)
  const [settings, setSettings] = useState([])
  const [drafts, setDrafts] = useState({})
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(true)
  const [authorizing, setAuthorizing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [creatingAdmin, setCreatingAdmin] = useState(false)
  const [admin, setAdmin] = useState({ name: '', email: '', password: '' })
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [updatingUserId, setUpdatingUserId] = useState(null)

  const redirectIfUnauthorized = (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(PLATFORM_TOKEN_KEY)
      navigate('/plataforma/login', { replace: true })
      return true
    }
    return false
  }

  const load = () => {
    setLoading(true)
    return Promise.all([
      platformApi.get(`/api/platform/organizations/${id}`),
      platformApi.get(`/api/platform/organizations/${id}/settings`),
    ])
      .then(([orgResponse, settingsResponse]) => {
        const nextOrganization = orgResponse.data.organization
        setOrganization(nextOrganization)
        if (nextOrganization?.status === 'active') loadUsers()
        const nextSettings = settingsResponse.data.settings || []
        setSettings(nextSettings)
        const nextDrafts = {}
        nextSettings.forEach((item) => {
          nextDrafts[item.key] = item.secret ? '' : (item.value || '')
        })
        setDrafts(nextDrafts)
      })
      .catch((err) => {
        if (redirectIfUnauthorized(err)) return
        setError(platformErrorMessage(err, 'Não foi possível abrir a organização'))
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const groups = useMemo(() => {
    const map = new Map()
    settings.forEach((item) => {
      if (!map.has(item.group)) map.set(item.group, [])
      map.get(item.group).push(item)
    })
    return Array.from(map.entries())
  }, [settings])

  const authorize = async () => {
    const confirmed = window.confirm(
      'Autorizar cria o schema e copia só a estrutura das tabelas da empresa atual. Nenhum dado é copiado.'
    )
    if (!confirmed) return
    setAuthorizing(true)
    setError('')
    setNotice('')
    try {
      const { data } = await platformApi.post(`/api/platform/organizations/${id}/authorize`)
      setOrganization(data.organization)
      setNotice(`Tabelas criadas: ${data.tables}.`)
    } catch (err) {
      if (redirectIfUnauthorized(err)) return
      setError(platformErrorMessage(err, 'Não foi possível criar as tabelas'))
      load()
    } finally {
      setAuthorizing(false)
    }
  }

  const loadUsers = () => {
    setUsersLoading(true)
    return platformApi.get(`/api/platform/organizations/${id}/users`)
      .then(({ data }) => setUsers(data.users || []))
      .catch((err) => {
        if (redirectIfUnauthorized(err)) return
        setError(platformErrorMessage(err, 'Não foi possível listar os usuários'))
      })
      .finally(() => setUsersLoading(false))
  }

  const updateUser = async (user, patch, confirmMessage) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return
    setUpdatingUserId(user.id)
    setError('')
    setNotice('')
    try {
      await platformApi.patch(`/api/platform/organizations/${id}/users/${user.id}`, patch)
      setNotice('Usuário atualizado.')
      await loadUsers()
    } catch (err) {
      if (redirectIfUnauthorized(err)) return
      setError(platformErrorMessage(err, 'Não foi possível atualizar o usuário'))
    } finally {
      setUpdatingUserId(null)
    }
  }

  const createAdmin = async (event) => {
    event.preventDefault()
    setCreatingAdmin(true)
    setError('')
    setNotice('')
    try {
      await platformApi.post(`/api/platform/organizations/${id}/administrator`, admin)
      setNotice('Administrador criado. Ele entra pelo login do ERP, com esse e-mail e essa senha.')
      setAdmin({ name: '', email: '', password: '' })
      await loadUsers()
    } catch (err) {
      if (redirectIfUnauthorized(err)) return
      setError(platformErrorMessage(err, 'Não foi possível criar o usuário'))
    } finally {
      setCreatingAdmin(false)
    }
  }

  const save = async (event) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    setNotice('')
    const values = {}
    settings.forEach((item) => {
      const draft = drafts[item.key] ?? ''
      if (item.secret) {
        if (draft.trim()) values[item.key] = draft
        return
      }
      if (draft !== (item.value || '')) values[item.key] = draft
    })
    try {
      await platformApi.put(`/api/platform/organizations/${id}/settings`, { values })
      setNotice('Variáveis gravadas. Chaves secretas não são exibidas de novo.')
      await load()
    } catch (err) {
      if (redirectIfUnauthorized(err)) return
      setError(platformErrorMessage(err, 'Não foi possível gravar'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="platform-page">
      <header className="platform-topbar">
        <div>
          <h1>{organization?.trade_name || organization?.legal_name || 'Organização'}</h1>
          <p>
            {organization ? `${organization.schema_name} · ` : ''}
            {organization ? STATUS_LABEL[organization.status] || organization.status : 'Carregando'}
          </p>
        </div>
        <Link className="platform-button-secondary" to="/plataforma">Voltar</Link>
      </header>
      <main className="platform-body">
        {error ? <p className="platform-error">{error}</p> : null}
        {notice ? <p className="platform-note">{notice}</p> : null}
        {organization?.provision_error ? <p className="platform-error">{organization.provision_error}</p> : null}
        {organization && (organization.status === 'pending' || organization.status === 'failed') ? (
          <p>
            <button className="platform-button" type="button" onClick={authorize} disabled={authorizing}>
              {authorizing ? 'Criando tabelas…' : 'Autorizar e criar tabelas'}
            </button>
          </p>
        ) : null}
        {organization?.status === 'active' ? (
          <section className="platform-card platform-users">
            <h2>Usuários</h2>
            <p className="platform-note">
              O perfil de administrador só é definido aqui. No ERP, a tela de usuários cria contas comuns e grupos de acesso.
            </p>
            {usersLoading ? <p className="platform-note">Carregando usuários…</p> : null}
            {!usersLoading && users.length === 0 ? (
              <p className="platform-note">Nenhum usuário cadastrado nesta organização.</p>
            ) : null}
            {users.length > 0 ? (
              <div className="platform-table-wrap">
                <table className="platform-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>E-mail</th>
                      <th>Perfil</th>
                      <th>Grupo</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.role === 'admin' ? 'Administrador' : 'Usuário'}</td>
                        <td>{user.access_group_name || '—'}</td>
                        <td>{user.active === false ? 'Inativo' : 'Ativo'}</td>
                        <td className="platform-table-actions">
                          <button
                            type="button"
                            className="platform-button-secondary"
                            disabled={updatingUserId === user.id}
                            onClick={() => updateUser(
                              user,
                              { role: user.role === 'admin' ? 'user' : 'admin' },
                              user.role === 'admin'
                                ? `Remover o acesso de administrador de ${user.name}?`
                                : `Tornar ${user.name} administrador?`
                            )}
                          >
                            {user.role === 'admin' ? 'Remover admin' : 'Tornar admin'}
                          </button>
                          <button
                            type="button"
                            className="platform-button-secondary"
                            disabled={updatingUserId === user.id}
                            onClick={() => updateUser(
                              user,
                              { active: user.active === false },
                              user.active === false
                                ? `Reativar ${user.name}?`
                                : `Desativar ${user.name}?`
                            )}
                          >
                            {user.active === false ? 'Reativar' : 'Desativar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        ) : null}
        {organization?.status === 'active' ? (
          <form className="platform-card platform-form" onSubmit={createAdmin}>
            <h2>Novo administrador</h2>
            <p className="platform-note">
              Este administrador nasce só nesta organização. Para aparecer no dropdown do ERP,
              o mesmo e-mail precisa existir nas duas, com acesso ao ERP em cada uma.
            </p>
            <label>
              Nome
              <input
                value={admin.name}
                onChange={(event) => setAdmin((current) => ({ ...current, name: event.target.value }))}
                required
              />
            </label>
            <label>
              E-mail
              <input
                type="email"
                value={admin.email}
                onChange={(event) => setAdmin((current) => ({ ...current, email: event.target.value }))}
                required
              />
            </label>
            <label>
              Senha
              <input
                type="password"
                value={admin.password}
                onChange={(event) => setAdmin((current) => ({ ...current, password: event.target.value }))}
                minLength={8}
                required
              />
            </label>
            <button className="platform-button" type="submit" disabled={creatingAdmin}>
              {creatingAdmin ? 'Criando…' : 'Criar administrador'}
            </button>
          </form>
        ) : null}
        <p className="platform-note">
          Senha do banco, host e a chave mestra do cofre continuam só nas variáveis do Render.
          Uma chave secreta, depois de salva, aparece como configurada e o valor não volta para a tela.
        </p>
        {loading ? <p className="platform-note">Carregando variáveis…</p> : null}
        {!loading && settings.length > 0 ? (
          <form onSubmit={save}>
            {groups.map(([group, items]) => (
              <section className="platform-group" key={group}>
                <h3>{group}</h3>
                <div className="platform-card">
                  {items.map((item) => (
                    <label className="platform-setting" key={item.key}>
                      <span>
                        {item.label}
                        <small>
                          {item.key}
                          {item.secret && item.configured ? ' · configurada' : ''}
                        </small>
                        {item.hint ? <small>{item.hint}</small> : null}
                      </span>
                      <input
                        type={item.secret ? 'password' : 'text'}
                        autoComplete="off"
                        placeholder={item.secret && item.configured ? 'Deixe em branco para manter' : ''}
                        value={drafts[item.key] ?? ''}
                        onChange={(event) => {
                          const value = event.target.value
                          setDrafts((current) => ({ ...current, [item.key]: value }))
                        }}
                      />
                    </label>
                  ))}
                </div>
              </section>
            ))}
            <p>
              <button className="platform-button" type="submit" disabled={saving}>
                {saving ? 'Salvando…' : 'Salvar variáveis'}
              </button>
            </p>
          </form>
        ) : null}
      </main>
    </div>
  )
}

export default OrganizationManagePage
